/* jshint esversion: 6 */
import React from 'react';
import PropTypes from 'prop-types';
import ConfirmPostRecentModal from 'app/components/common/ConfirmPostRecentModal.jsx';
import CreateFeedOutputPanel from 'app/components/EditorComponents/CreateFeedOutputPanel.jsx';
import RouteProvider from 'app/redux/Providers/RouteProvider.jsx';
import SocialSelection from 'app/components/SocialModal/SocialSelection.jsx';
import TypeSelection from 'app/components/SocialModal/TypeSelection.jsx';
import ConnectSocialDialog from '@/shadcn/components/AddSocialModal/ConnectSocialDialog.tsx';
import ConfirmModal from 'app/components/common/ConfirmModal.jsx';
import MastodonHandle from 'app/components/SocialModal/MastodonHandle.jsx';
import BlueskyCreds from 'app/components/SocialModal/BlueskyCreds.jsx';
import { SOCIAL_UPDATE } from 'app/redux/constants.js';
import * as _ from 'underscore';

class SocialModal extends React.Component {
	constructor(props) {
		super(props);

		let selectedSocials = [],
			originalSet = [];

		if (props.selectedSocials && Array.isArray(props.selectedSocials)) {
			selectedSocials = [...props.selectedSocials];
			originalSet = [...props.selectedSocials];
		}

		this.state = {
			originalSet: originalSet,
			selectedSocials: selectedSocials,
			activeTab: props.activeTab,
			context: 'list',
			errors: [],
			isDirty: false,
			serviceId: 0,
			twitterMaxedOut: false,
			mainTweeter: 0,
			inceptionModal: null,
			pendingSelectId: 0
		};

		this.switchView = this.switchView.bind(this);
		this.setInceptionModal = this.setInceptionModal.bind(this);
		this.socialClickHandler = this.socialClickHandler.bind(this);
		this.saveDialog = this.saveDialog.bind(this);
		this.saveDialogConfirm = this.saveDialogConfirm.bind(this);
		this.saveDialogCancel = this.saveDialogCancel.bind(this);
		this.onSocialsSelected = this.onSocialsSelected.bind(this);
		this.getSocialsToAdd = this.getSocialsToAdd.bind(this);
		this.saveHandler = this.saveHandler.bind(this);
		this.setMainTweeter = this.setMainTweeter.bind(this);
		this.resetTwitters = this.resetTwitters.bind(this);
		this.closeAction = this.closeAction.bind(this);
		this.onReactivate = this.onReactivate.bind(this);
		this.onSocialUpdated = this.onSocialUpdated.bind(this);
		this.updateCallback = {action: SOCIAL_UPDATE, func: this.onSocialUpdated};

	}

	static getDerivedStateFromProps(props, state) {
		let nextState = {},
			selectedSocials = [],
			originalSet = [],
			constants = props.app.config.constants;

		if (state.originalSet.length === 0 && props.outputs.length) {
			for (let i = 0; i < props.outputs.length; ++i) {
				selectedSocials.push(props.outputs[i].social.id);
				originalSet.push(props.outputs[i].social.id);
				if (props.outputs[i].role == constants.OR_ROLE_NORMAL && props.outputs[i].social.psID == constants.POST_SVC_TWITTER) {
					nextState.mainTweeter = props.outputs[i].social.id;
				}
			}

			if (selectedSocials.length) {
				nextState.selectedSocials = selectedSocials;
			}

			if (originalSet.length) {
				nextState.originalSet = originalSet;
			}

		}

		return (Object.keys(nextState).length) ? nextState : null;

	}

	componentDidMount() {

		// reset any prior sort and filter
		this.props.clearFilter('socials');
		this.props.sortStore('socials', 'name', 'ASC');

		// get types list
		this.props.getSocialTypes();

		/*
		 * Registered rather than watched here, because what this needs to know is
		 * that the save came back — not that some prop changed. SocialProvider owns
		 * the loading-flag transition for every social write; this asks it for the
		 * one it cares about. Guarded because a couple of hosts render this modal
		 * outside the provider's prop chain.
		 */
		if (typeof this.props.registerCallback === 'function') {
			this.props.registerCallback(this.updateCallback);
		}

	}

	componentWillUnmount() {
		if (this.props.clearFilterOnClose) {
			this.props.clearFilter('socials');
		}

		if (typeof this.props.unregisterCallback === 'function') {
			this.props.unregisterCallback(this.updateCallback);
		}
	}

	/*
	 * A social write came back. The only one this screen is waiting on is the
	 * reactivation it just asked for: select that account now that there is
	 * something to select, which is what the click that started all this was
	 * asking for in the first place.
	 *
	 * `detail.changed` is what makes that safe to assume. Any number of things
	 * can write a social while this dialog is open, and without knowing which
	 * account came back the only options were to act on someone else's answer or
	 * to give up waiting for our own. Matching on the id does neither: a write
	 * about a different account is ignored and this one keeps waiting, and a
	 * write of ours that failed or changed nothing appears in no one's `changed`,
	 * so it also just keeps waiting — until the account is either reactivated for
	 * real or the dialog closes and takes the question with it.
	 *
	 * Selection goes through socialClickHandler rather than straight into state,
	 * so the account arrives under the same rules as a normal click — the X cap,
	 * the main-tweeter assignment and the dirty check all still apply.
	 */
	onSocialUpdated(detail) {
		let social = null;

		if (!this.state.pendingSelectId) {
			return;
		}

		social = _.find(detail.changed, (candidate) => {
			return candidate.id === this.state.pendingSelectId;
		});

		// Still inactive means the server declined to bring it back, whatever the
		// response said.
		if (social === undefined || parseInt(social.activeFlag, 10) === 0) {
			return;
		}

		this.setState({pendingSelectId: 0}, () => {
			if (this.state.selectedSocials.indexOf(social.id) !== -1) {
				return;
			}

			this.socialClickHandler({
				currentTarget: {
					dataset: {
						id: String(social.id),
						type: String(social.psID)
					}
				}
			});
		});

	}

	/*
	 * No redux filtering here any more. The account screen narrows its own list
	 * from `socials.list` — by the chosen service and the typed query — so a
	 * store-wide filter would have no reader inside the dialog, while still
	 * filtering the page underneath it, which is visible the moment the dialog
	 * is dismissed. `serviceId` is state, and that is all the screen needs.
	 */
	switchView(view, serviceId) {
		this.setState({
			activeTab: view,
			serviceId: serviceId || 0
		});
	}

	setInceptionModal(modal) {
		this.setState({
			inceptionModal: modal
		});
	}

	socialClickHandler(e) {
		let newId = parseInt(e.currentTarget.dataset.id, 10),
			idList = this.state.selectedSocials,
			twitterMaxedOut = false,
			index = 0,
			isDirty = this.state.isDirty,
			i = 0,
			diff = [],
			constants = this.props.app.config.constants,
			twitterCount = 0,
			test = null,
			mainTweeter = this.state.mainTweeter;

		if (newId !== 0 && idList.find((id) => { return newId === id; }) === undefined) {
			let social = _.find(this.props.socials.list, (social) => {
				return newId == social.id
			});
			if (social && social.psID == constants.POST_SVC_TWITTER) {
				for (let key in idList) {
					test = _.find(this.props.socials.list, (social) => {
						return idList[key] == social.id;
					});
					if (test !== undefined) {
						if (test.psID == constants.POST_SVC_TWITTER) {
							++twitterCount;
						}
					}
				}
				let nextTwitterCount = twitterCount + 1;
				if (nextTwitterCount >= constants.MAX_TWITTER_OUTPUTS) {
					twitterMaxedOut = true;
				}
				if (twitterCount >= constants.MAX_TWITTER_OUTPUTS) {
					return;
				}
				if (twitterCount == 0) {
					mainTweeter = newId;
				}
				if (twitterCount >= 1 && this.props.singleTwitter) {
					return;
				}
			}

			idList.push(newId);
		} else {
			idList = _.without(idList, newId);
		}

		// run a quick check to see if a save is actually needed
		// or we're back to where we started
		isDirty = false;
		diff = _.difference(idList, this.state.originalSet);
		if (diff.length || idList.length < this.state.originalSet.length) {
			isDirty = true;
		}

		this.setState({
			isDirty: isDirty,
			selectedSocials: idList,
			twitterMaxedOut: twitterMaxedOut,
			mainTweeter: mainTweeter
		});

	}

	saveDialog() {
		let toAdd = this.getSocialsToAdd();

		// only show the postRecentDialog if a routeId is present
		// otherwise it's being used in a new route creation
		if (toAdd.length && this.props.routeId) {
			this.setState({ activeTab: 'postRecentDialog' });
		} else {
			this.saveHandler(0);
		}

	}

	saveDialogConfirm() {

		this.props.clearFilter('socials');
		this.props.closeAllModals();

		return this.saveHandler(1);

	}

	saveDialogCancel() {

		this.props.clearFilter('socials');
		this.props.closeAllModals();

		return this.saveHandler(0);

	}

	onSocialsSelected(e) {

		e.preventDefault();

		if (this.props.onSocialsSelected) {
			this.props.onSocialsSelected.apply(this, [this.state.selectedSocials]);
		}

	}

	getSocialsToAdd() {
		return _.difference(this.state.selectedSocials, this.state.originalSet);
	}

	saveHandler(postRecent) {

		let toAdd = [],
			toRemove = [],
			outputsToRemove = [],
			i = 0,
			constants = this.props.app.config.constants;


		toAdd = this.getSocialsToAdd();

		toRemove = _.difference(this.state.originalSet, this.state.selectedSocials);
		if (toRemove.length) {
			for (i = 0; i < this.props.outputs.length; ++i) {
				if (toRemove.indexOf(this.props.outputs[i].social.id) !== -1) {
					outputsToRemove.push(this.props.outputs[i].id);
				}
			}
		}

		// allow the interface to update the selected socials when creating a new route
		if (this.props.updateSelectedSocials !== undefined && typeof this.props.updateSelectedSocials === "function") {
			this.props.updateSelectedSocials({
				add: toAdd,
				remove: toRemove
			});
		}

		if (this.props.routeId) {
			// if (this.state.isDirty && toAdd.length === 0 && toRemove.length === 0 && this.state.mainTweeter) {
			// 	let output = _.find(this.props.outputs, function(output) {
			// 		return output.social.id == this.state.mainTweeter
			// 	}.bind(this));
			// 	if (output) {
			// 		output.role = constants.OR_ROLE_NORMAL;
			// 		this.props.updateOutput(output);
			// 	}
			//
			// } else {
			for (i = 0; i < outputsToRemove.length; ++i) {
				this.props.deleteOutput(outputsToRemove[i]);
			}


			for (i = 0; i < toAdd.length; ++i) {
				let output = {
						rID: this.props.routeId,
						paID: toAdd[i],
						activeFlag: true,
						postRecent: postRecent
					};

				if (toAdd[i] == this.state.mainTweeter) {
					output.role = constants.OR_ROLE_NORMAL;
				}
				this.props.createOutput(output);
			}


			//}

		}

		this.props.closeAllModals();

	}

	setMainTweeter(id) {
		let selectedSocials = this.state.selectedSocials,
			isDirty = this.state.isDirty;

		if (id) {
			if (selectedSocials.indexOf(id) === -1) {
				selectedSocials.push(id);
				isDirty = true;
			}
			if (id != this.state.mainTweeter) {
				isDirty = true;
			}
		}
		this.setState({
			mainTweeter: id,
			selectedSocials: selectedSocials,
			isDirty: isDirty
		});

	}

	resetTwitters() {
		let confirm = function() {
				let constants = this.props.app.config.constants,
					socials = this.state.selectedSocials,
					newSocials = [];

				for (let i = 0; i < socials.length; ++i) {
					let social = _.find(this.props.socials.list, function(social){
						return social.id == socials[i];
					}.bind(this));
					if (social && social.psID != constants.POST_SVC_TWITTER) {
						newSocials.push(socials[i]);
					}
				}

				this.setState({
					selectedSocials: newSocials,
					mainTweeter: 0,
					inceptionModal: null,
					isDirty: true
				});

			}.bind(this),
			cancel = function() {
				this.setInceptionModal(null);
			}.bind(this),

			modal = (
				<ConfirmModal
					cancelAction={cancel}
					confirmAction={confirm}
					modalCopy={"Deselecting your main Twitter will remove all retweeters too. Click on the main account in the tray to change it instead."}/>
			);

			this.setInceptionModal(modal);

	}

	/*
	 * The user confirmed the reactivate prompt. The account is not selectable
	 * until the save lands, so its id is parked for onSocialUpdated to spend.
	 */
	onReactivate(social) {
		this.setState({
			pendingSelectId: social.id
		});
	}

	/* The one screen whose title isn't about a network. */
	modalTitle() {
		if (this.props.context === 'bulk-modal') {
			return 'Import to Socials';
		}
		if (this.props.context === 'reconnect') {
			return 'Reconnect your social';
		}

		return 'Connect Social';

	}

	/*
	 * The retweet notice, shown to someone adding their first extra X account:
	 * new ones retweet the main tweeter rather than posting on their own, which is
	 * surprising enough to say out loud before they save.
	 */
	footerStatus() {
		let constants = this.props.app.config.constants;

		if (this.state.serviceId !== constants.POST_SVC_TWITTER) {
			return null;
		}

		for (let i = 0; i < this.props.socials.counts.length; ++i) {
			if (this.props.socials.counts[i].type == constants.POST_SVC_TWITTER &&
				this.props.socials.counts[i].count === 1) {
				return (
					<a href="https://support.dlvrit.com/hc/en-us/articles/360004803754" target="_blank" rel="noreferrer">
						New X (Twitter) accounts will post as retweets. Learn More.
					</a>
				);
			}
		}

		return null;

	}

	closeAction() {
		if (this.props.closeCallback !== undefined) {
			this.props.closeCallback();
		}

		this.props.closeAllModals();

	}

	twitterInput() {
		let constants = this.props.app.config.constants;

		if (this.props.activeRoute === undefined ||
			this.props.activeRoute.inputs === undefined ||
			this.props.activeRoute.inputs.length === undefined) {
			return false;
		}

		for (let i = 0; i < this.props.activeRoute.inputs.length; ++i) {
			if (this.props.activeRoute.inputs[i].type !== undefined &&
				this.props.activeRoute.inputs[i].type == constants.INPUT_TYPE_TWITTER) {
				return true;
			}
		}

		return false;

	}

	twitterCount() {
		let constants = this.props.app.config.constants,
			count = 0;

		for (let i = 0; i < this.state.selectedSocials.length; ++i) {
			let social = _.find(this.props.socials.list, function(social) {
				return this.state.selectedSocials[i] == social.id
			}.bind(this));

			if (social !== undefined && social.psID == constants.POST_SVC_TWITTER) {
				++count;
			}
		}

		return count;

	}

	render() {
		let activeTab = this.state.activeTab,
			twitterInput = this.twitterInput(),
			selectionContext = 'modal',
			dialogProps = {};

		if (this.props.context === 'bulk-modal' || this.props.context === 'list-add') {
			selectionContext = this.props.context;
		}

		/*
		 * The most-recent-item prompt replaces the dialog rather than opening over
		 * it — it's the last question of the save, asked once the choosing is done.
		 */
		if (activeTab === 'postRecentDialog') {
			let toAdd = this.getSocialsToAdd(),
				message = (toAdd.length > 1)
					? 'Would you like to share the most recent item to these socials?'
					: 'Would you like to share the most recent item to this social?';

			return (
				<ConfirmPostRecentModal
					modalCopy={message}
					cancelAction={this.saveDialogCancel}
					confirmAction={this.saveDialogConfirm} />
			);
		}

		/*
		 * Three tabs draw their own dialog and are returned as-is: the footer on
		 * those screens belongs to the panel, since what Connect does, when it is
		 * enabled and where Back returns to are all its decisions.
		 *
		 * There was a fourth, `choosesubtype`, asking whether a Facebook destination
		 * was a Page or a Group. Pages are the only kind we can connect now, so the
		 * picker connects one directly and nothing routed there any more.
		 */
		if (activeTab === 'providehandle') {
			return (
				<MastodonHandle
					{...this.props}
					onboarding={this.props.onboarding}
					switchView={this.switchView}/>
			);
		}

		if (activeTab === 'blueskycreds') {
			return (
				<BlueskyCreds
					{...this.props}
					onboarding={this.props.onboarding}
					switchView={this.switchView}/>
			);
		}

		if (activeTab === 'createfeed') {
			return (
				<CreateFeedOutputPanel
					{...this.props}
					switchView={this.switchView}
					onboarding={this.props.onboarding}
					twitterInput={twitterInput} />
			);
		}

		/*
		 * The two screens this component owns. The picker advances on a mark, so
		 * it has no footer; the account list needs one, and its primary is the
		 * save.
		 */
		if (activeTab === 'chooseAcct') {
			dialogProps = {
				screen: 'accounts',
				subtitle: 'Choose an account, or connect a new one.',
				backLabel: 'Back',
				primaryLabel: (this.props.context === 'bulk-modal') ? 'Choose socials' : 'Update',
				primaryDisabled: !this.state.isDirty,
				footerStatus: this.footerStatus(),
				showFooter: true,
				// The importer opens straight onto the list, so there is no screen
				// behind it for Back to return to.
				showBack: (this.props.context !== 'bulk-modal'),
				onBack: () => { this.switchView('chooseType'); },
				onPrimary: (this.props.context === 'bulk-modal')
					? this.onSocialsSelected
					: this.saveDialog,
				body: (
					<SocialSelection
						{...this.props}
						variant="dialog"
						context={selectionContext}
						routeId={this.props.routeId}
						outputs={this.props.outputs}
						socialClickHandler={this.socialClickHandler}
						connectHandler={this.switchView}
						saveHandler={this.saveDialog}
						isDirty={this.state.isDirty}
						selectedSocials={this.state.selectedSocials}
						serviceId={this.state.serviceId}
						redirect={this.props.redirect}
						recordPath={this.props.recordPath}
						twitterInput={twitterInput}
						twitterCount={this.twitterCount()}
						singleTwitter={this.props.singleTwitter}
						twitterMaxedOut={this.state.twitterMaxedOut}
						mainTweeter={this.state.mainTweeter}
						setMainTweeter={this.setMainTweeter}
						resetTwitters={this.resetTwitters}
						switchView={this.switchView}
						allowReconnect={true}
						onReactivate={this.onReactivate} />
				)
			};
		} else {
			dialogProps = {
				screen: 'network',
				subtitle: 'Pick a network to connect your profile',
				body: (
					<TypeSelection
						{...this.props}
						tile={true}
						types={this.props.socials.types}
						switchView={this.switchView}
						recordPath={this.props.recordPath}
						router={this.props.router}
						redirect={this.props.redirect}
						onboarding={this.props.onboarding}
						twitterInput={twitterInput} />
				)
			};
		}

		let {body, ...chrome} = dialogProps;

		return (
			<ConnectSocialDialog
				title={this.modalTitle()}
				onClose={this.closeAction}
				{...chrome}>
				{/*
				  * The retweet-reset confirm, inside the dialog's own subtree on
				  * purpose. It's a legacy `.dlvr-modal` — fixed, z-index 1001, so it
				  * paints over the card — but the dialog's box is pointer-events-none
				  * so that a click beside the card can dismiss it, and anything
				  * outside the card would inherit that and be unclickable.
				  */}
				{this.state.inceptionModal}
				{body}
			</ConnectSocialDialog>
		);
	}
};

SocialModal.displayName = "SocialModal";

SocialModal.propTypes = {
	outputs: PropTypes.array,
	routeId: PropTypes.oneOfType([
		PropTypes.number,
		PropTypes.string
	]),
	setModal: PropTypes.func,
	router: PropTypes.object,
	context: PropTypes.string,
	recordPath: PropTypes.func,
	redirect: PropTypes.string,
	activeTab: PropTypes.string,
	onboarding: PropTypes.object,
	connectHandler: PropTypes.func,
	trialEligible: PropTypes.bool,
	twitterInput: PropTypes.bool,
	clearFilterOnClose: PropTypes.bool
};

SocialModal.defaultProps = {
	outputs: [],
	routeId: 0,
	setModal: null,
	router: null,
	context: '',
	redirect: '',
	activeTab: 'chooseType',
	onboarding: null,
	connectHandler: null,
	trialEligible: false,
	twitterInput: false,
	singleTwitter: false,
	noTwitter: false,
	clearFilterOnClose: false
};

export default RouteProvider(SocialModal);
